import type { Appearance, StripeElementLocale } from "@stripe/stripe-js";

/**
 * How Stripe's own frames are dressed so they read as part of the sheet.
 *
 * Stripe draws the card fields and the wallet button inside iframes that no stylesheet of ours
 * can reach, so it takes an appearance object of literal values instead. That is the one place
 * in this app where a colour or a length is a string rather than a class, and it is why this
 * file exists: every value below is READ from the tokens at runtime with `getComputedStyle` and
 * the rest are DERIVED from them, so light and dark both hold, a token change reaches the card
 * fields with no second edit, and no hex and no off-scale number is typed into a UI file.
 *
 * `stripeAppearance()` is called again on every mount, because `getComputedStyle` answers for
 * the theme that is on at the moment it runs.
 */

/** True while the root carries the dark theme; the line tokens differ there. */
function isDark(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.dataset.theme === "dark";
}

function token(name: string, fallback = ""): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

/** A pixel token as a number, so a length can be derived from it rather than typed beside it. */
function pxToken(name: string, fallback: number): number {
  const parsed = Number.parseFloat(token(name));
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** The control scale's hand step: the height of every field on a money form, and of the wallet. */
export const CONTROL_LARGE = 48;
export const WALLET_HEIGHT = CONTROL_LARGE;

/**
 * Where Stripe fetches our faces from: the same files the page itself loads, at the absolute
 * origin, because the fetch happens inside an iframe on js.stripe.com.
 *
 * Two things have to be true for it to land, and both are now: the stylesheet and the woff2
 * files answer cross-origin (public/_headers in production, `server.headers` in dev), and the
 * origin is https. Over plain http://localhost the iframe is https and the faces are http, so
 * the browser blocks them as mixed content and Stripe's fields fall back to the default face.
 * That is a property of the dev origin and not of this code; the CSS is fetched, the faces are
 * not, and there is nothing to fix on an https origin.
 */
export function fontSource(): { cssSrc: string } {
  return { cssSrc: `${window.location.origin}/fonts/fonts.css` };
}

/** i18n's language as a Stripe locale; anything else lets Stripe follow the browser. */
export function stripeLocale(lang: string): StripeElementLocale {
  const known: StripeElementLocale[] = ["en", "fr", "es", "de", "pt", "it"];
  const base = lang.split("-")[0] as StripeElementLocale;
  return known.includes(base) ? base : "auto";
}

export function stripeAppearance(): Appearance {
  /* In dark, `--gk-line-hairline` and `--gk-line-strong` are ALPHA lines, and an alpha line
     doubles up wherever surfaces stack (DS32, gate 9.5). The stylesheet re-points every border
     to the solid pair under [data-theme="dark"]; Stripe's iframe cannot read that rule, so the
     same choice is made here and the card fields draw the same edge as the fields above them. */
  const dark = isDark();
  const hairline = dark
    ? token("--gk-line-hairline-solid", token("--gk-line-hairline"))
    : token("--gk-line-hairline");
  const strong = dark
    ? token("--gk-line-strong-solid", token("--gk-line-strong"))
    : token("--gk-line-strong");
  const orange = token("--gk-line-orange");
  const danger = token("--gk-semantic-danger");
  const dangerText = token("--gk-semantic-danger-text");
  const tintOrange15 = token("--gk-tint-orange15");

  const controlHeight = pxToken("--gk-control-large", CONTROL_LARGE);
  const borderWidth = pxToken("--gk-border-hairline", 1);
  const radius = pxToken("--gk-radius-control", 8);
  const unit = pxToken("--gk-space-1", 4);
  const padX = pxToken("--gk-space-4", 16);
  const labelGap = pxToken("--gk-space-2", 8);
  const valueSize = 15;
  /* Stripe's input is a 15/1.2 line inside our hairline. The vertical padding is not a number
     anyone picked: it is whatever lands the well on the control height the fields beside it
     take, so the card row and the email row are the same object. */
  const padY = Math.max(
    0,
    (controlHeight - Math.round(valueSize * 1.2) - borderWidth * 2) / 2,
  );

  return {
    theme: "flat",
    variables: {
      /* Figtree first, exactly as the @font-face of /fonts/fonts.css names it, then a real
         fallback stack. Stripe accepts a stack and only needs the face named somewhere in it;
         with the bare name and nothing after it, an origin where the face cannot load (an http
         dev origin, where the https iframe refuses our http CSS as mixed content) drops the
         card fields to the browser's own default, which is a serif. */
      fontFamily: "Figtree, system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontSizeBase: `${valueSize}px`,
      colorPrimary: orange,
      colorBackground: token("--gk-surface-paper"),
      colorText: token("--gk-text-primary"),
      colorTextSecondary: token("--gk-text-secondary"),
      colorTextPlaceholder: token("--gk-text-tertiary"),
      colorDanger: dangerText,
      /* The tab and accordion glyphs inherit the line's ink; `colorPrimary` is the focus line
         and nothing else, so a selected payment method is not a second orange (gate 3.1). */
      colorIconTab: token("--gk-text-secondary"),
      colorIconTabSelected: token("--gk-text-primary"),
      colorIconTabHover: token("--gk-text-primary"),
      borderRadius: `${radius}px`,
      spacingUnit: `${unit}px`,
    },
    rules: {
      ".Input": {
        border: `${borderWidth}px solid ${hairline}`,
        padding: `${padY}px ${padX}px`,
        boxShadow: "none",
      },
      ".Input:hover": { borderColor: strong },
      ".Input:focus": {
        borderColor: orange,
        boxShadow: `0 0 0 ${unit}px ${tintOrange15}`,
      },
      /* Invalid is the danger line and the sentence under it, never a red fill (text-input 1.3
         doNot 2). */
      ".Input--invalid": { borderColor: danger, boxShadow: "none" },
      ".Label": {
        fontSize: "13px",
        color: token("--gk-text-secondary"),
        marginBottom: `${labelGap}px`,
      },
      ".Error": { color: dangerText, fontSize: "13px" },
      /* With one payment method Stripe draws no tab bar and no accordion header at all, because
         the Elements group is opened with `paymentMethodTypes: ["card"]`. These rules are the
         belt to that brace: if a second method ever arrives, its chrome is a row on the sheet
         and not a white card with an orange title inside our card (gate 6.2 for the nested
         surface, 3.1 for the second orange). */
      ".Tab": {
        border: "none",
        boxShadow: "none",
        backgroundColor: "transparent",
        padding: "0",
      },
      ".AccordionItem": {
        border: "none",
        boxShadow: "none",
        backgroundColor: "transparent",
        padding: "0",
      },
      ".TabIcon--selected": { color: token("--gk-text-primary") },
      ".TabLabel--selected": { color: token("--gk-text-primary") },
      ".AccordionItem--selected": { color: token("--gk-text-primary") },
      ".Block": {
        border: `${borderWidth}px solid ${hairline}`,
        boxShadow: "none",
      },
    },
  };
}
